import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, RefreshCw, Loader2, CheckCircle2, ChevronDown } from 'lucide-react'
import { useAIFetch } from '../../hooks/useAI.jsx'
import { useAuth } from '../../hooks/useAuth'
import AgentAvatar from './AgentAvatar.jsx'
import { useTranslation } from 'react-i18next'

const SEVERITY_STYLES = {
  urgent: { dot: 'bg-red-500', row: 'hover:bg-red-50' },
  normal: { dot: 'bg-amber-400', row: 'hover:bg-amber-50' },
  ok: { dot: 'bg-green-500', row: 'hover:bg-green-50' },
}

/**
 * AIActionGuide — "what should I do next" panel for every role dashboard.
 * Each item is AI-generated from the user's real state and deep-links to
 * the page where they can act. Renders nothing when AI is unavailable.
 */
export default function AIActionGuide() {
  const { t } = useTranslation('assistant')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, loading, refresh } = useAIFetch('/ai/action-guide', [])
  // Collapsed preference is PER USER (a global key shared it across everyone on the browser).
  const collapseKey = `aiguide-collapsed:${user?.id || user?.email || 'anon'}`
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(collapseKey) === '1')

  const toggle = () => {
    setCollapsed(c => {
      const next = !c
      localStorage.setItem(collapseKey, next ? '1' : '0')
      return next
    })
  }

  // No key configured → render nothing (feature invisible, not broken)
  if (data && data.available === false) return null

  const itemCount = data?.items?.length || 0

  return (
    <div className="card overflow-hidden border border-indigo-100">
      <div className="px-5 py-3 bg-linear-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 flex items-center justify-between">
        {/* Clicking the header (or chevron) collapses/expands the list */}
        <button onClick={toggle} className="flex items-center gap-2.5 text-left flex-1 min-w-0" title={collapsed ? 'Show next actions' : 'Hide next actions'}>
          <AgentAvatar size={32} animated />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-indigo-900 truncate">
              ProTrack Agent — Your Next Actions
              {collapsed && itemCount > 0 && (
                <span className="ml-2 text-[10px] font-bold text-white bg-indigo-500 rounded-full px-1.5 py-0.5 align-middle">{itemCount}</span>
              )}
            </h3>
            <p className="text-[10px] text-indigo-400 truncate">
              {collapsed ? 'Tap to see what needs you' : "I've checked your projects. Here's what needs you:"}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {!collapsed && (
            <button
              onClick={refresh}
              disabled={loading}
              className="p-1.5 text-indigo-400 hover:text-indigo-700 hover:bg-white rounded-lg transition disabled:opacity-40"
              title={t('t_refresh')}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={toggle}
            className="p-1.5 text-indigo-400 hover:text-indigo-700 hover:bg-white rounded-lg transition"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
          </button>
        </div>
      </div>

      {collapsed ? null : loading && !data ? (
        <div className="flex items-center justify-center py-8 gap-2 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Analysing your projects…
        </div>
      ) : !data?.items?.length ? (
        <div className="flex items-center justify-center py-8 gap-2 text-sm text-gray-400">
          <CheckCircle2 className="w-4 h-4 text-green-500" /> Nothing pending — you're all caught up.
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {data.items.map((item, i) => {
            const s = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.normal
            return (
              <button
                key={i}
                onClick={() => item.href && navigate(item.href)}
                className={`w-full flex items-start gap-3 px-5 py-3 text-left transition ${s.row}`}
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${s.dot} ${item.severity === 'urgent' ? 'animate-pulse' : ''}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                  {item.detail && <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>}
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 shrink-0 mt-1" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
